export const QUOTATION_TEMPLATES_TOOL_SLUG = "quotation-calculator";
export const QUOTATION_SUMMARY_TEMPLATE_KEY = "whatsapp_summary";
export const QUOTATION_TEMPLATES_SCHEMA_VERSION = 3;

export const PAYMENT_TEMPLATE_GROUPS = ["pix", "cartao", "outros"] as const;
export type PaymentTemplateGroup = (typeof PAYMENT_TEMPLATE_GROUPS)[number];

export function isPaymentTemplateGroup(
	value: unknown,
): value is PaymentTemplateGroup {
	return (
		typeof value === "string" &&
		(PAYMENT_TEMPLATE_GROUPS as readonly string[]).includes(value)
	);
}

export interface QuotationTemplateMatch {
	categoriaItem: number[];
	paymentFlow: PaymentTemplateGroup[];
}

export interface QuotationTemplateItem {
	key: string;
	label: string;
	body: string;
	active: boolean;
	isDefault: boolean;
	priority: number;
	match: QuotationTemplateMatch;
}

export interface QuotationTemplatesConfig {
	schemaVersion: number;
	templates: QuotationTemplateItem[];
}

export interface QuotationTemplateContext {
	categoriaItem?: number | null;
	paymentFlow?: PaymentTemplateGroup | null;
}

export interface ParsedQuotationTemplatesConfig {
	config: QuotationTemplatesConfig;
	issues: string[];
	usedFallback: boolean;
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function normalizeNumberArray(value: unknown): number[] {
	if (!Array.isArray(value)) return [];
	const unique = new Set<number>();
	for (const item of value) {
		const parsed = Number(item);
		if (Number.isInteger(parsed) && parsed > 0) unique.add(parsed);
	}
	return [...unique];
}

function normalizePaymentFlowArray(value: unknown): PaymentTemplateGroup[] {
	if (!Array.isArray(value)) return [];
	const unique = new Set<PaymentTemplateGroup>();
	for (const item of value) {
		if (isPaymentTemplateGroup(item)) unique.add(item);
	}
	return [...unique];
}

function normalizeTemplate(
	input: Record<string, unknown>,
	index: number,
): QuotationTemplateItem | null {
	if (
		typeof input.key !== "string" ||
		typeof input.label !== "string" ||
		typeof input.body !== "string" ||
		typeof input.active !== "boolean"
	) {
		return null;
	}

	const matchInput = isObjectRecord(input.match) ? input.match : {};
	const priority = Number(input.priority);

	return {
		key: input.key.trim() || `template_${index + 1}`,
		label: input.label.trim() || `Template ${index + 1}`,
		body: input.body,
		active: input.active,
		isDefault: Boolean(input.isDefault),
		priority: Number.isInteger(priority) && priority >= 0 ? priority : 0,
		match: {
			categoriaItem: normalizeNumberArray(matchInput.categoriaItem),
			paymentFlow: normalizePaymentFlowArray(matchInput.paymentFlow),
		},
	};
}

function hasContextMatch(
	template: QuotationTemplateItem,
	context: QuotationTemplateContext,
): boolean {
	const { categoriaItem, paymentFlow } = template.match;

	if (categoriaItem.length > 0) {
		if (!context.categoriaItem) return false;
		if (!categoriaItem.includes(context.categoriaItem)) return false;
	}

	if (paymentFlow.length > 0) {
		if (!context.paymentFlow) return false;
		if (!paymentFlow.includes(context.paymentFlow)) return false;
	}

	return true;
}

function isUnconstrained(template: QuotationTemplateItem): boolean {
	return (
		template.match.categoriaItem.length === 0 &&
		template.match.paymentFlow.length === 0
	);
}

function matchSpecificity(template: QuotationTemplateItem): number {
	let score = 0;
	if (template.match.categoriaItem.length > 0) score += 1;
	if (template.match.paymentFlow.length > 0) score += 1;
	return score;
}

function sortByPriorityThenDefault(
	left: QuotationTemplateItem,
	right: QuotationTemplateItem,
): number {
	if (right.priority !== left.priority) return right.priority - left.priority;
	const specificityDelta = matchSpecificity(right) - matchSpecificity(left);
	if (specificityDelta !== 0) return specificityDelta;
	if (right.isDefault !== left.isDefault)
		return Number(right.isDefault) - Number(left.isDefault);
	return left.label.localeCompare(right.label);
}

/**
 * Normaliza a config vinda do banco. Mantem compatibilidade com v1
 * (sem schemaVersion, isDefault, priority ou match).
 */
export function parseQuotationTemplatesConfig(
	input: unknown,
): ParsedQuotationTemplatesConfig {
	const issues: string[] = [];

	if (!isObjectRecord(input) || !Array.isArray(input.templates)) {
		return {
			config: { schemaVersion: QUOTATION_TEMPLATES_SCHEMA_VERSION, templates: [] },
			issues: ["Configuracao de templates ausente ou invalida."],
			usedFallback: true,
		};
	}

	const templates: QuotationTemplateItem[] = [];
	input.templates.forEach((candidate, index) => {
		if (!isObjectRecord(candidate)) {
			issues.push("Um template veio em formato invalido e foi ignorado.");
			return;
		}
		const normalized = normalizeTemplate(candidate, index);
		if (normalized) {
			templates.push(normalized);
		} else {
			issues.push("Um template veio incompleto e foi ignorado.");
		}
	});

	if (templates.length > 0 && !templates.some((item) => item.isDefault)) {
		templates[0] = { ...templates[0], isDefault: true };
	}

	return {
		config: {
			schemaVersion: QUOTATION_TEMPLATES_SCHEMA_VERSION,
			templates,
		},
		issues,
		usedFallback: templates.length === 0,
	};
}

/**
 * Seleciona o corpo do template de resumo (WhatsApp) pelo contexto atual.
 * Templates ativos que casam a categoria concorrem por priority.
 * Sem match, cai no default ativo. Sem default, retorna null para fallback.
 */
export function selectSummaryTemplateBody(
	config: QuotationTemplatesConfig | null | undefined,
	context: QuotationTemplateContext = {},
	templateKey: string = QUOTATION_SUMMARY_TEMPLATE_KEY,
): string | null {
	if (!config?.templates?.length) return null;

	const activeTemplates = config.templates.filter(
		(item) =>
			item.active &&
			item.body.trim() &&
			(item.key === templateKey || item.key !== templateKey),
	);
	if (activeTemplates.length === 0) return null;

	const matched = activeTemplates
		.filter((item) => hasContextMatch(item, context))
		.sort(sortByPriorityThenDefault);
	if (matched[0]?.body?.trim()) return matched[0].body;

	const fallback = activeTemplates
		.filter((item) => item.isDefault || isUnconstrained(item))
		.sort(sortByPriorityThenDefault)[0];
	return fallback?.body?.trim() ? fallback.body : null;
}

/**
 * Retorna a key do template vencedor para o contexto atual, ou null se nenhum
 * template ativo for encontrado.
 */
export function selectSummaryTemplateKey(
	config: QuotationTemplatesConfig | null | undefined,
	context: QuotationTemplateContext = {},
	_templateKey?: string,
): string | null {
	if (!config?.templates?.length) return null;

	const activeTemplates = config.templates.filter(
		(item) => item.active && item.body.trim(),
	);
	if (activeTemplates.length === 0) return null;

	const matched = activeTemplates
		.filter((item) => hasContextMatch(item, context))
		.sort(sortByPriorityThenDefault);
	if (matched[0]) return matched[0].key;

	const fallback = activeTemplates
		.filter((item) => item.isDefault || isUnconstrained(item))
		.sort(sortByPriorityThenDefault)[0];
	return fallback?.key ?? null;
}

export function createQuotationTemplateKey(label: string, existingKeys: string[]): string {
	const base = label
		.trim()
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "") || "template";
	let key = base;
	let suffix = 2;
	while (existingKeys.includes(key)) {
		key = `${base}_${suffix}`;
		suffix += 1;
	}
	return key;
}

export function getQuotationRuntimeSharedConfigEndpoint(): string {
	return `/tools/${QUOTATION_TEMPLATES_TOOL_SLUG}/shared-config`;
}

export function getQuotationAdminSharedConfigEndpoint(): string {
	return `/admin/tools/${QUOTATION_TEMPLATES_TOOL_SLUG}/shared-config`;
}
